import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { type ColumnDef } from '@tanstack/react-table';
import { api, throwIfNotOk } from '../../lib/api';
import { qk } from '../../lib/queryKeys';
import { useAuth } from '../../lib/authContext';
import { QueryBoundary } from '@siliconx/shared/ui/QueryBoundary';
import { DataTable } from '@siliconx/shared/ui/DataTable';
import { PageHeader } from '@siliconx/shared/ui/PageHeader';
import { MyActivity } from '../../components/exchange/activity/MyActivity';

export const Route = createFileRoute('/transactions/')({
  component: TransactionsPage,
});

async function fetchTransactions() {
  const res = await api.api.client.transactions.$get();
  await throwIfNotOk(res);
  const { data } = await res.json();
  return data;
}

type TransactionRow = Awaited<ReturnType<typeof fetchTransactions>>[number];

const idColumn: ColumnDef<TransactionRow, unknown> = {
  id: 'id',
  header: 'Transaction ID',
  cell: (ctx) => (
    <span className="font-mono text-xs text-muted-foreground">
      {ctx.row.original.id.slice(0, 8)}&hellip;
    </span>
  ),
};

const skuColumn: ColumnDef<TransactionRow, unknown> = {
  id: 'sku',
  header: 'SKU',
  cell: (ctx) => (
    <span className="text-foreground">
      {ctx.row.original.skuShortDescription ?? ctx.row.original.skuId ?? '—'}
    </span>
  ),
};

const conditionColumn: ColumnDef<TransactionRow, unknown> = {
  id: 'conditionBand',
  header: 'Condition',
  cell: (ctx) => <span className="text-muted-foreground">{ctx.row.original.conditionBand ?? '—'}</span>,
};

const closedAtColumn: ColumnDef<TransactionRow, unknown> = {
  id: 'closedAt',
  header: 'Closed',
  cell: (ctx) => (
    <span className="text-muted-foreground">
      {ctx.row.original.closedAt
        ? new Date(ctx.row.original.closedAt).toLocaleDateString()
        : '—'}
    </span>
  ),
};

const SELLER_COLUMNS: ColumnDef<TransactionRow, unknown>[] = [
  idColumn,
  skuColumn,
  conditionColumn,
  {
    id: 'salePriceUsd',
    header: 'Sale Price',
    cell: (ctx) => (
      <div className="text-right font-medium text-foreground">
        ${Number(ctx.row.original.salePriceUsd).toLocaleString()}
      </div>
    ),
  },
  {
    id: 'netToSellerUsd',
    header: 'Net to Seller',
    cell: (ctx) => (
      <div className="text-right text-muted-foreground">
        {ctx.row.original.netToSellerUsd
          ? `$${Number(ctx.row.original.netToSellerUsd).toLocaleString()}`
          : '—'}
      </div>
    ),
  },
  closedAtColumn,
];

function SellerTransactionsPage() {
  const transactionsQ = useQuery({
    queryKey: qk.transactions.all,
    queryFn: fetchTransactions,
  });

  return (
    <div>
      {/* Transactions is a self-evident list page (Earned-Subtitle Rule) — no subtitle. */}
      <PageHeader title="Transactions" pinTo="/transactions" className="mb-6" />
      <QueryBoundary query={transactionsQ} errorTitle="Couldn't load transactions." empty={() => false}>
        {(data) => <DataTable data={data} columns={SELLER_COLUMNS} emptyMessage="No transactions yet." />}
      </QueryBoundary>
    </div>
  );
}

function TransactionsPage() {
  const { orgType } = useAuth();
  // SIL-1351: a buyer's orders live on the My Activity surface (spec section
  // 4.6), which this route and /offers both render — see MyActivity's header
  // for why one component backs two routes. The seller table is unchanged.
  if (orgType === 'buyer') return <MyActivity pinTo="/transactions" />;
  return <SellerTransactionsPage />;
}
